'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { SignIn, useAuth } from '@clerk/nextjs';
import { AUTH_DOOR_COPY } from '@/constants/authCopy';
import { NEXT_QUERY_KEY } from '@/lib/authRouting';
import type { UtmAttribution } from '@/lib/utmCookies';

type Variant = 'login' | 'register';

/**
 * unsafeMetadata as OUR doors write it. The global SignUpUnsafeMetadata leaves
 * `segment` optional because it types reads too, and Clerk hands back signups
 * that never passed through a door (OAuth transfer, invite acceptance) — but
 * nothing this component mints may go out unsegmented (531-1442).
 */
export type SegmentedSignupMetadata = SignUpUnsafeMetadata & Required<Pick<SignUpUnsafeMetadata, 'segment'>>;

type DoorConfig = {
	path: string;
	segment: SegmentedSignupMetadata['segment'];
	/**
	 * Where an already-signed-in visitor goes instead of seeing the door. Null means the
	 * sign-in target (the router, with the deep link) — /login's case.
	 */
	signedInRedirectUrl: string | null;
	signUpForceRedirectUrl: string;
	header: { title: string; subtitle: string };
	crossLink: { prompt: string; label: string; href: string; pendo: string };
};

/**
 * Combined sign-in/sign-up surface for both auth entries (531-1034, replaces
 * the 531-986 /auth page). The ROUTE is the segment — /login=b2c,
 * /register=b2b — which retired the 531-1018 toggle and its key={segment}
 * remount + draft-email salvage (531-1022). UTM attribution and the segment
 * ride unsafeMetadata; the user.created webhook promotes segment to
 * publicMetadata.
 *
 * Redirect targets deliberately live OUTSIDE both route subtrees and
 * prefix-match neither `path` prop (clerk-js probes prefix-matched targets
 * with GET <target>/SignIn_clerk_catchall_check_<ts> and internal-navigates
 * on a 200 — see src/app/auth-success/page.tsx).
 *
 * 531-1695: SIGN-INS on both doors are FORCED to /auth-success. Clerk's
 * precedence is forceRedirectUrl > the `redirect_url` search param >
 * fallbackRedirectUrl, so with only a fallback (the pre-1695 shape) a sign-in
 * that arrived via the proxy's `/login?redirect_url=` bounce skipped the
 * router entirely and landed on the deep link with Clerk's newest-org default
 * active. The door reads `redirect_url` (the page passes it through
 * safeNextPath) and hands it to the router as `?next=`, which the router
 * honours on the workspace landing. SIGN-UPS keep their door-specific
 * targets: /login → the marked router; /register → /org/new?from=signup
 * (register intent is org creation; ?from=signup keeps the middleware
 * session-build retry covering the user.created webhook race). Before 1695
 * /register sign-INS also landed on /org/new, which pushed a returning
 * member into creating a second org.
 */
const VARIANTS = {
	login: {
		path: '/login',
		segment: 'b2c',
		signedInRedirectUrl: null,
		signUpForceRedirectUrl: '/auth-success?from=signup',
		header: AUTH_DOOR_COPY.login,
		crossLink: {
			// Verb-neutral: /register serves both new and returning team users
			// (531-1034 review) — a returning member signs in and lands in their
			// workspace, a new one creates an org.
			prompt: 'Setting up a team?',
			label: 'Start here',
			href: '/register',
			pendo: 'auth:cross-link:register',
		},
	},
	register: {
		path: '/register',
		segment: 'b2b',
		signedInRedirectUrl: '/org/new?from=signup',
		signUpForceRedirectUrl: '/org/new?from=signup',
		header: AUTH_DOOR_COPY.register,
		crossLink: {
			prompt: 'Just for yourself?',
			label: 'Start solo',
			href: '/login',
			pendo: 'auth:cross-link:login',
		},
	},
} as const satisfies Record<Variant, DoorConfig>;

export function AuthEntryClient({
	variant,
	utmData,
	next,
}: {
	variant: Variant;
	utmData: UtmAttribution | null;
	/** The deep link the proxy bounce carried, already passed through safeNextPath by the page. */
	next: string | null;
}) {
	const router = useRouter();
	const { isLoaded, isSignedIn } = useAuth();
	const config = VARIANTS[variant];
	const unsafeMetadata: SegmentedSignupMetadata = {
		...(utmData ? { utm: utmData } : {}),
		segment: config.segment,
	};
	const forceRedirectUrl = next ? `/auth-success?${NEXT_QUERY_KEY}=${encodeURIComponent(next)}` : '/auth-success';
	const signedInRedirectUrl = config.signedInRedirectUrl ?? forceRedirectUrl;

	useEffect(() => {
		if (isLoaded && isSignedIn) {
			router.replace(signedInRedirectUrl);
		}
	}, [signedInRedirectUrl, isLoaded, isSignedIn, router]);

	// A soft back-navigation can restore the page from the Next router cache
	// without rerunning the proxy redirect. Do not mount Clerk's <SignIn> for an
	// authenticated user while the client-side redirect completes.
	if (isLoaded && isSignedIn) {
		return (
			<div className="min-h-screen bg-background flex items-center justify-center">
				<div
					className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent"
					aria-label="Redirecting"
				/>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-background flex flex-col items-center justify-center gap-6 py-10">
			<header className="max-w-md px-4 text-center space-y-2">
				<h1 className="text-2xl font-semibold tracking-tight">{config.header.title}</h1>
				<p className="text-sm text-muted-foreground">{config.header.subtitle}</p>
			</header>
			<SignIn
				withSignUp
				routing="path"
				path={config.path}
				unsafeMetadata={unsafeMetadata}
				forceRedirectUrl={forceRedirectUrl}
				signUpForceRedirectUrl={config.signUpForceRedirectUrl}
				appearance={{
					// The page owns the surface: `flush` drops the card's border,
					// shadow, radius and outer padding, and the stock logo is suppressed
					// so only the owned headline above speaks.
					//
					// The start step's Clerk heading is suppressed in globals.css, NOT
					// here: `elements` has no per-step scope (headerTitle takes no id),
					// and every step of the combined flow renders its heading through
					// these same descriptors — hiding them here took 'Check your email'
					// and 'Reset your password' down with the start step (531-1442).
					options: { elevation: 'flush', logoPlacement: 'none' },
					elements: {
						rootBox: 'max-w-md mx-auto',
						formButtonPrimary: 'bg-primary text-primary-foreground hover:bg-primary/90',
						footerActionLink: 'text-primary hover:text-primary/90',
					},
				}}
			/>
			<p className="text-sm text-muted-foreground">
				{config.crossLink.prompt}{' '}
				<Link
					href={config.crossLink.href}
					data-pendo={config.crossLink.pendo}
					className="text-primary hover:text-primary/90 underline underline-offset-4"
				>
					{config.crossLink.label} →
				</Link>
			</p>
		</div>
	);
}
